import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Mail, MailOpen, ExternalLink } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_LABELS } from "@/data/site";
import Logo from "@/components/site/Logo";
import api from "@/lib/api";

const emptyProject = { title: "", category: "web", client: "", year: "", description: "", image_url: "", link: "", featured: false };

function ProjectForm({ initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    if (!form.title || !form.image_url) {
      toast.error("Titre et image (URL) requis.");
      return;
    }
    setSaving(true);
    try {
      if (form.id) await api.put(`/portfolio/${form.id}`, form);
      else await api.post("/portfolio", form);
      toast.success("Projet enregistré.");
      onSaved();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Input placeholder="Titre du projet" value={form.title} onChange={set("title")} data-testid="project-title-input" />
      <div className="grid grid-cols-2 gap-4">
        <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
          <SelectTrigger data-testid="project-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="web">Site web</SelectItem>
            <SelectItem value="print">Design papier</SelectItem>
            <SelectItem value="vehicle">Marquage véhicule</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Année" value={form.year} onChange={set("year")} data-testid="project-year-input" />
      </div>
      <Input placeholder="Client" value={form.client} onChange={set("client")} data-testid="project-client-input" />
      <Input placeholder="URL de l'image" value={form.image_url} onChange={set("image_url")} data-testid="project-image-input" />
      <Input placeholder="Lien (optionnel)" value={form.link} onChange={set("link")} data-testid="project-link-input" />
      <Textarea placeholder="Description" value={form.description} onChange={set("description")} data-testid="project-desc-input" />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} data-testid="project-featured-input" />
        Mettre en avant (page d'accueil)
      </label>
      <DialogFooter>
        <Button onClick={save} disabled={saving} data-testid="project-save-button">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
      </DialogFooter>
    </div>
  );
}

export default function Admin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(emptyProject);

  const load = useCallback(async () => {
    const [p, m] = await Promise.all([api.get("/portfolio"), api.get("/contact/messages")]);
    setProjects(p.data);
    setMessages(m.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = () => { setDialogOpen(false); load(); };

  const deleteProject = async (id) => {
    if (!window.confirm("Supprimer ce projet ?")) return;
    await api.delete(`/portfolio/${id}`);
    toast.success("Projet supprimé.");
    load();
  };

  const toggleRead = async (m) => {
    if (!m.read) await api.patch(`/contact/messages/${m.id}/read`);
    load();
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    await api.delete(`/contact/messages/${id}`);
    load();
  };

  const doLogout = async () => { await logout(); navigate("/login"); };
  const unread = messages.filter((m) => !m.read).length;

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="admin-page">
      <header className="bg-ink text-white">
        <div className="mx-auto max-w-[1300px] px-6 lg:px-10 py-5 flex items-center justify-between">
          <Logo className="h-10" white />
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-white/60 hover:text-white text-sm hidden sm:inline-flex items-center gap-1">
              Voir le site <ExternalLink size={14} />
            </a>
            <Button variant="secondary" size="sm" onClick={doLogout} data-testid="logout-button">
              <LogOut size={15} className="mr-2" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1300px] px-6 lg:px-10 py-10">
        <h1 className="font-display font-extrabold tracking-tighter text-4xl mb-8">Tableau de bord</h1>

        <Tabs defaultValue="projects">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="projects" data-testid="tab-projects">Portfolio ({projects.length})</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">
              Messages {unread > 0 && <Badge className="ml-2 bg-violet">{unread}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* PROJECTS */}
          <TabsContent value="projects" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => setEditing(emptyProject)} data-testid="add-project-button">
                    <Plus size={16} className="mr-2" /> Nouveau projet
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editing.id ? "Modifier le projet" : "Nouveau projet"}</DialogTitle>
                    <DialogDescription>Renseignez les informations du projet. Titre et URL d'image obligatoires.</DialogDescription>
                  </DialogHeader>
                  <ProjectForm initial={editing} onSaved={onSaved} />
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {projects.map((p) => (
                <div key={p.id} className="bg-white border border-black/10" data-testid={`admin-project-${p.id}`}>
                  <div className="aspect-video bg-secondary overflow-hidden">
                    <img src={p.image_url} alt={p.title} className="h-full w-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary">{CATEGORY_LABELS[p.category]}</Badge>
                      {p.featured && <Badge className="bg-violet">À la une</Badge>}
                    </div>
                    <h3 className="font-display font-bold text-lg">{p.title}</h3>
                    <p className="text-sm text-muted-foreground">{p.client} {p.year && `· ${p.year}`}</p>
                    <div className="mt-4 flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`edit-${p.id}`}>
                        <Pencil size={14} className="mr-1" /> Modifier
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteProject(p.id)} data-testid={`delete-${p.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages" className="mt-6">
            {messages.length === 0 ? (
              <p className="text-muted-foreground py-16 text-center">Aucun message pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`bg-white border p-5 ${m.read ? "border-black/10" : "border-violet"}`}
                    data-testid={`message-${m.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-display font-bold text-lg">{m.name}</h3>
                          {!m.read && <span className="h-2 w-2 rounded-full bg-violet" />}
                        </div>
                        <a href={`mailto:${m.email}`} className="text-violet text-sm hover-line">{m.email}</a>
                        {m.phone && <span className="text-muted-foreground text-sm ml-3">{m.phone}</span>}
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {new Date(m.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    {m.subject && <p className="mt-3 font-medium">{m.subject}</p>}
                    <p className="mt-1 text-muted-foreground whitespace-pre-wrap">{m.message}</p>
                    <div className="mt-4 flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => toggleRead(m)} data-testid={`toggle-read-${m.id}`}>
                        {m.read ? <MailOpen size={14} className="mr-1" /> : <Mail size={14} className="mr-1" />}
                        {m.read ? "Lu" : "Marquer comme lu"}
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteMessage(m.id)} data-testid={`delete-msg-${m.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
